import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CognitoUser } from '@alucio/beacon/src/models/User';
import { userActions } from '../../state/redux/slice/user';
import store, { storeReset } from '../../state/redux/store';
import ActiveUser from 'src/state/global/ActiveUser'
import { AlucioChannel } from '@alucio/lux-ui';
import workerChannel from 'src/worker/channels/workerChannel'
// AMPLIFY
import awsConfig from '@alucio/aws-beacon-amplify/src/aws-exports';
import { DataStore } from '@aws-amplify/datastore';
import Auth from '@aws-amplify/auth'
import { Amplify, Hub, Logger } from '@aws-amplify/core'
import { Authenticator as AmplifyAuthenticator, withAuthenticator } from './Authenticator';
import config from 'src/config/app.json';

// Custom Authentication Screens
import SignIn from 'src/screens/Authentication/SignIn/SignIn';
import RequireNewPassword from 'src/screens/Authentication/Password/RequireNewPassword/RequireNewPassword';
import ForgotPassword from 'src/screens/Authentication/Password/ForgotPassword/ForgotPassword';
import { SessionStatus } from '../IdleComponent/IdleComponent';
import { setDisconnectedDue } from 'src/components/Authenticator/DisconnectedDue';
import { CognitoIdToken } from 'amazon-cognito-identity-js';
import { clear } from 'src/utils/analytics/queueIdb';
import { withStore } from 'src/utils/analytics/index';
import { useAppSettings } from 'src/state/context/AppSettings';
import { WebLinking as Linking } from '@alucio/core';
const logger = new Logger('Authenticator', 'INFO');

Amplify.configure({
  ...awsConfig,
  Analytics: { disabled: true },
  // Set Amplify to send ID Token when it's available (user logged in). This allows us to use the tenantID
  // within amplify @auth attributes for filtering records to the user's tenant
  API: {
    graphql_headers: async () => {
      return new Promise((resolve) => {
        Auth.currentSession().then((session) => {
          resolve({ Authorization: session.getIdToken().getJwtToken() })
        }).catch(() => {
          resolve({})
        })
      })
    },
  },
  oauth: {
    domain: `${config.authUrl}.auth.us-west-2.amazoncognito.com`,
    scope: ['phone', 'email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
    redirectSignIn: `${window.location.origin}/`,
    redirectSignOut: `${window.location.origin}/`,
    responseType: 'code',
  },
});

interface AuthProps {
  authState?: string;
  authData?: CognitoUser;
  onStateChange: (status: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const LogoutContext = createContext({ signOut: (reason?: SessionStatus, fromChannel?: boolean) => { } });
export const useLogout = () => useContext(LogoutContext);
LogoutContext.displayName = 'LogoutContext';

// Alternatively, don't use the HOC and use the components instead
// https://aws-amplify.github.io/docs/js/authentication#using-the-authenticator-component
const Authenticator: React.FC<AuthProps> = props => {
  const { children } = props;
  const { isOfflineEnabled } = useAppSettings();
  logger.debug('Authenticator Rendered')
  async function signOut(reason = SessionStatus.end, fromChannel = false) {
    logger.debug('signOut method called')
    if (!fromChannel) {
      setDisconnectedDue(reason);
      AlucioChannel.get('beacon-idle')?.postMessage(reason);
    }
    props.onStateChange('signedOut');
    localStorage.removeItem('amplify-latest-user-attributes');
    AlucioChannel.remove('beacon-idle');
    store.dispatch(storeReset())
    ActiveUser.clear()
    // For all IndexedDB operations we only run them from one tab the one
    // which initiated the logout
    if (!fromChannel) {
      if (isOfflineEnabled) {
        // Pause syncing
        await workerChannel.postMessageExtended({ type: 'PAUSE_SYNC' })
      }
      await DataStore.clear()
      await clear(withStore);
    }
    Hub.dispatch('logout', { event: 'logout' });
    // This should be the last action since it might redirect the user away (for SSO logout)
    await Auth.signOut()
    if (fromChannel) {
      Linking.openURL(`/?refresh=${Date.now()}&logoutFor=${reason}`, '_self');
    }
  }
  useEffect(() => {
    // Listen for Auth SignIn event and clear out datastore to handle case if session expires
    // and user signs in again
    // We use a separate AuthListener rather than adding it to the useEffect code below
    // to handle switch from Browser -> PWA (BEAC-2453)
    const authListener = async (data) => {
      switch (data.payload.event) {
        case 'signIn':
          store.dispatch(storeReset())
          await DataStore.clear()
          ActiveUser.clear()
          break
      }
    }
    const removeListener = Hub.listen('auth', authListener);

    return () => removeListener();
  }, [isOfflineEnabled]);

  useEffect(() => {
    logger.debug('useEffect onAuthState/authData Called', props)
    const init = async (props: AuthProps) => {
      if (props.authState === 'signedIn' && props.authData) {
        const userAttributtes = new CognitoIdToken({
          IdToken: props
            .authData
            // @ts-ignore with the sso we didnt detect a way to get the attributes from the user so we are using the id token for tha
            .signInUserSession
            .idToken
            .jwtToken,
        })
          .decodePayload()

        const attributes = (props.authData.attributes || userAttributtes) as any;
        store.dispatch(
          userActions.setCognitoUser({ username: props.authData.username, attributes: attributes }),
        );
        localStorage.setItem('amplify-latest-user-attributes', JSON.stringify({
          username: props.authData.username, attributes,
        }));
      }
    }
    init(props)
  }, [props.authState, props.authData]);
  return <LogoutContext.Provider value={{ signOut }}>{children}</LogoutContext.Provider>;
};

// ON FIRST LOAD, AMPLIFY CHECKS THE ACCESS TOKEN. IF IT'S EXPIRED, AMPLIFY TRIES TO REFRESH IT.
// IF IT FAILS REFRESHING IT (WHICH WILL HAPPEN IF OFFLINE), IT LOGS OUT THE USER.
// WE WANT TO HANDLE THAT SCENARIO SO IF THE LAST AMPLIFY STATUS IS VALID, WE DON'T LOG THEM OUT.
const PWAAuthenticator: React.FC = (props) => {
  const { isOnline } = useAppSettings();
  const onLoadAmplifyAuthState = useMemo(
    () => localStorage.getItem('amplify-authenticator-authState') || undefined, [],
  )
  const [cognitoState, setCognitoState] = useState({
    authState: !isOnline ? onLoadAmplifyAuthState : undefined,
    authData: undefined,
  });

  useEffect(() => {
    async function handleOffLineLoad(): Promise<void> {
      let cognitoUser;
      try {
        cognitoUser = await Auth.currentAuthenticatedUser();
      } catch (e) {
        logger.debug('Authenticated user call failed, ', e);
        logger.debug('Getting CognitoUser from LocalStorage');
        cognitoUser = JSON.parse(localStorage.getItem('amplify-latest-user-attributes') ?? '');
      }

      if (!cognitoUser) {
        logger.debug('CognitoUser from LocalStorage not found');
        logger.debug('Signing Out');
        await Auth.signOut();
        setCognitoState({ authState: undefined, authData: undefined });
        return;
      }

      store.dispatch(
        userActions.setCognitoUser({ username: cognitoUser.username, attributes: cognitoUser.attributes }),
      );
    }

    if (!isOnline && onLoadAmplifyAuthState === 'signedIn') {
      handleOffLineLoad();
    }
  }, []);

  function handleAuthStateChange(state, data): void {
    setCognitoState({ authState: state, authData: data });
  }

  function onStateChange(state): void {
    setCognitoState((currentState) => ({ ...currentState, authState: state }));
  }

  const MemoizedAuthenticator = useMemo(() => (
    <Authenticator
      authState={cognitoState.authState}
      authData={cognitoState.authData}
      onStateChange={onStateChange}
    >
      {props.children}
    </Authenticator>
  ), [cognitoState]);

  if (cognitoState.authState !== 'signedIn') {
    return (
      <AmplifyAuthenticator
        hideDefault={true}
        onStateChange={handleAuthStateChange}
        children={[
          <SignIn key="custom-sign-in" />,
          <RequireNewPassword key="require-new-password" />,
          <ForgotPassword key="forgot-password" />,
        ]}
      />
    );
  }

  return MemoizedAuthenticator;
};

const AuthenticatorWrapper: React.FC = (props) => {
  const { isPWAStandalone } = useAppSettings();

  const DesktopAuthenticator = useMemo(() => withAuthenticator(Authenticator, false, [
    <SignIn key="custom-sign-in" />,
    <RequireNewPassword key="require-new-password" />,
    <ForgotPassword key="forgot-password" />,
  ]), []);

  return isPWAStandalone ? <PWAAuthenticator {...props} /> : <DesktopAuthenticator {...props} />;
};

export default AuthenticatorWrapper;
